/**
 * Account-deletion screen — Owner-only (Requirement 25.5–25.8).
 *
 * Lets an Owner request account deletion after seeing the retention-rule
 * consequences and giving an explicit irreversibility confirmation. Account
 * deletion is a *backend* concept (erase every PII-tagged field except records
 * under a retention obligation; block entirely when any record's retention
 * status is undeterminable — Req 18). Per the module boundary, `web` imports no
 * backend runtime: the actual delete call is INJECTED as a prop, and this screen
 * only gates submission on the confirmation and presents the Platform's response
 * through the accessible {@link UserMessage}.
 *
 * Requirement mapping:
 *   - 25.5 Owner sees the retention-rule consequences (which categories of
 *          records are retained under an obligation) and MUST give an explicit
 *          affirmative irreversibility confirmation before the submit control is
 *          enabled.
 *   - 25.6 On a confirmed deletion the Platform accepts, a success User_Message
 *          confirms the request and RESTATES the categories of records retained
 *          (Req 18.5 — completion notes categories retained).
 *   - 25.7 On a denial/failure a "not deleted" error User_Message states
 *          what/why/next; the mapped envelope carries the reason. A blocked
 *          deletion (Req 18.2 — undeterminable retention status,
 *          `RETENTION_STATUS_UNKNOWN`) is surfaced here as that error.
 *   - 25.8 A non-Owner sees only a restricted User_Message and no controls;
 *          authorization is enforced by the Platform regardless — the hidden
 *          controls are never the sole gate.
 *   - 22.11 Every user-facing string resolves through the i18n catalog (`useT`).
 *   - 26.x  All feedback is rendered through {@link UserMessage}.
 *
 * SSR note: the retention consequences, the confirmation checkbox, and the
 * (disabled-until-confirmed) submit control are in the initial render output, so
 * the server-rendered HTML contains real content (Req 21.1). Async results
 * update local state on the client after hydration.
 */
import { useCallback, useState, type ReactElement } from "react";
// Type-only import from the shared foundation contract (web imports types only).
import type { ErrorEnvelope } from "@avos/foundation";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/** The four tenant RBAC roles (Req 12.1). Only `Owner` may use this screen. */
export type TenantRole = "Owner" | "Manager" | "Staff" | "Accountant";

/**
 * The result of the injected account-deletion call.
 *
 * On success the backend returns the categories of records retained under a
 * retention obligation, which the screen restates to the Owner (Req 25.6 /
 * 18.5). On failure it carries the stable {@link ErrorEnvelope} (e.g.
 * `RETENTION_STATUS_UNKNOWN` when deletion is blocked — Req 18.2) that the
 * screen maps to a User_Message via {@link toUserMessage} (Req 25.7).
 */
export type DeleteAccountResult =
  | { readonly ok: true; readonly retainedCategories: readonly string[] }
  | { readonly ok: false; readonly error: ErrorEnvelope };

/** Props for {@link AccountDeletionScreen}. */
export interface AccountDeletionScreenProps {
  /**
   * The signed-in user's tenant role. Only `Owner` sees the controls (Req 25.8).
   * Optional and fail-closed: when the host has not injected a role (e.g. the
   * bare screen-registry render used by the SSR shell), it defaults to a
   * NON-Owner view so no controls leak. The host (and tests) inject the real role.
   */
  readonly role?: TenantRole;
  /**
   * The categories of records retained under a retention obligation, shown as
   * the retention-rule consequences BEFORE deletion (Req 25.5) and used as a
   * fallback restatement on success when the Platform returns none. Injected by
   * the host; defaults to empty.
   */
  readonly retainedCategories?: readonly string[];
  /**
   * Injected account-deletion call. Web imports no backend runtime, so the
   * actual deletion is provided by the host. Optional so the screen can render
   * its consequences/controls in SSR/isolation without a wired backend; the
   * submit action is only enabled once confirmed AND a handler is present.
   */
  readonly onDeleteAccount?: () => Promise<DeleteAccountResult>;
}

/** What kind of feedback the screen is currently showing. */
type Feedback =
  | { readonly kind: "none" }
  | { readonly kind: "error"; readonly model: UserMessageModel }
  | {
      readonly kind: "status";
      readonly model: UserMessageModel;
      readonly retained: readonly string[];
    };

export default function AccountDeletionScreen({
  role = "Staff",
  retainedCategories = [],
  onDeleteAccount,
}: AccountDeletionScreenProps = {}): ReactElement {
  const t = useT();

  // The explicit irreversibility acknowledgement (Req 25.5). Submit stays
  // disabled until this is checked. Never true during SSR (default false).
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback>({ kind: "none" });
  // Whether an injected call is in flight (disables controls, prevents double
  // submit). Never true during SSR.
  const [busy, setBusy] = useState<boolean>(false);

  const handleDelete = useCallback(async () => {
    // Guard: submission is only possible once the irreversibility confirmation
    // is given (Req 25.5) and a handler is wired.
    if (!confirmed || onDeleteAccount === undefined) {
      return;
    }
    setBusy(true);
    try {
      const result = await onDeleteAccount();
      if (result.ok) {
        // Req 25.6 / 18.5: confirm the request and RESTATE the categories of
        // records retained. Prefer the Platform's list; fall back to the
        // injected consequences when none are returned.
        const retained =
          result.retainedCategories.length > 0
            ? result.retainedCategories
            : retainedCategories;
        setFeedback({
          kind: "status",
          retained,
          model: {
            what: t("admin.deletion.success.what"),
            why: t("admin.deletion.success.why"),
            next: t("admin.deletion.success.next"),
          },
        });
      } else {
        // Req 25.7 / 18.2: not deleted; present the Platform's reason. A
        // blocked deletion (RETENTION_STATUS_UNKNOWN) surfaces here.
        setFeedback({ kind: "error", model: toUserMessage(result.error) });
      }
    } finally {
      setBusy(false);
    }
  }, [confirmed, onDeleteAccount, retainedCategories, t]);

  // Non-Owner: restricted message only, no controls (Req 25.8). The Platform
  // still enforces authorization; the hidden controls are never the sole gate.
  if (role !== "Owner") {
    return (
      <section
        aria-labelledby="screen-heading"
        data-screen="accountDeletion"
        data-restricted="true"
      >
        <h1 id="screen-heading">{t("admin.deletion.title")}</h1>
        <UserMessage
          severity="status"
          message={{
            what: t("admin.deletion.restricted.what"),
            why: t("admin.deletion.restricted.why"),
            next: t("admin.deletion.restricted.next"),
          }}
        />
      </section>
    );
  }

  const canSubmit = confirmed && !busy;

  return (
    <section aria-labelledby="screen-heading" data-screen="accountDeletion">
      <h1 id="screen-heading">{t("admin.deletion.title")}</h1>
      <p>{t("admin.deletion.prompt")}</p>

      {/* Retention-rule consequences shown BEFORE enabling submit (Req 25.5):
          the categories of records retained under a retention obligation. */}
      <div className="account-deletion__consequences" data-testid="deletion-consequences">
        <p>{t("admin.deletion.consequences")}</p>
        <ul data-testid="deletion-retained-categories">
          {retainedCategories.map((category) => (
            <li key={category} data-testid="deletion-retained-category">
              {category}
            </li>
          ))}
        </ul>
      </div>

      {/* Explicit affirmative irreversibility confirmation (Req 25.5). */}
      <div className="account-deletion__confirm">
        <input
          id="account-deletion-confirm"
          name="acknowledgeIrreversible"
          type="checkbox"
          checked={confirmed}
          disabled={busy}
          data-testid="deletion-confirm"
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <label htmlFor="account-deletion-confirm">
          {t("admin.deletion.confirm")}
        </label>
      </div>

      {/* Submit stays disabled until the irreversibility confirmation is given
          (Req 25.5). */}
      <button
        type="button"
        data-testid="delete-account"
        disabled={!canSubmit}
        onClick={() => void handleDelete()}
      >
        {t("admin.deletion.submit")}
      </button>

      {feedback.kind === "status" ? (
        <div className="account-deletion__result">
          <UserMessage severity="status" message={feedback.model} />
          {/* Restate the categories of records retained (Req 25.6 / 18.5). */}
          <p data-testid="deletion-success-retained-heading">
            {t("admin.deletion.success.retainedHeading")}
          </p>
          <ul data-testid="deletion-success-retained">
            {feedback.retained.map((category) => (
              <li key={category} data-testid="deletion-success-retained-category">
                {category}
              </li>
            ))}
          </ul>
        </div>
      ) : feedback.kind === "error" ? (
        <UserMessage severity="error" message={feedback.model} />
      ) : null}
    </section>
  );
}
